/**
 * LES(물류실행 추상화 계층) 유래 입고건의 어드민 취소 차단 게이트. (LOGISPRD-12332 / 원 티켓 LOGISPRD-12051)
 *
 * moms-be 는 어드민 `POST /api/v1/cancel-inbound` 진입부(`CancelInboundLesOriginGuard`)에서 LES 유래
 * 건을 거부한다. 화면도 같은 조건으로 취소를 못 누르게 막아, 눌러 본 뒤 400 을 받는 일이 없게 한다.
 *
 * 판정축이 경로마다 다르다 — 어느 쪽을 쓸지는 그 화면이 무엇을 조회했는지로 갈린다.
 *
 * | 경로 | 테이블 | 판정 | BE 대응 |
 * | --- | --- | --- | --- |
 * | 입고예정(취소 API 가 직접 읽는 것) | `OPR_AN_STATUS` | `SRC_TABLE_NM = 'LES_INBOUND'` | `InboundPlan.isLesOrigin()` |
 * | 입고현황 조회(화면이 그리는 것) | `UI_GRREPORT_DETAIL` | `CUT_SEQ = 0` | `InboundReport.isLesOrigin()` |
 *
 * 조회 테이블에 출처 컬럼이 없어 BE 가 `cutSeq` 로 파생해 둔 것이고, 여기서 새로 만든 규칙이 아니다.
 * 화주코드 같은 다른 값으로 대신 판정하지 않는다.
 */

/**
 * LES 유래 입고예정의 `OPR_AN_STATUS.SRC_TABLE_NM` 값.
 * moms-be `InboundPlan.LES_ORIGIN_SRC_TABLE_NAME` 과 같은 값이다.
 */
export const LES_ORIGIN_SRC_TABLE_NAME = 'LES_INBOUND'

/**
 * LES 전용 컷 채번. moms-be `LesInboundPlanMapperImpl.LES_CUT_SEQUENCE` · `InboundReport.LES_ORIGIN_CUT_SEQ`
 * 와 같은 값이다.
 *
 * 같은 구간의 그 외 출처는 전량 1 이상이라 `cutSeq === 0` 이 곧 LES 출처다(BE dev 실측).
 */
export const LES_ORIGIN_CUT_SEQ = 0

/** BE 거부 코드. `MusinsaOmsApiCode.ERROR_LES_ORIGIN_CANCEL_NOT_ALLOWED`. */
export const LES_ORIGIN_CANCEL_ERROR_CODE = 'ERROR_LES_ORIGIN_CANCEL_NOT_ALLOWED'

/** 차단 사유 문구. BE 거부 메시지의 앞 문장과 같게 둔다 — 화면과 서버가 다른 말을 하면 안 된다. */
export const LES_ORIGIN_CANCEL_BLOCKED_MESSAGE = 'LES 로 적재된 입고건은 어드민에서 취소 지시를 내릴 수 없습니다.'

/** BE 차단 게이트의 Feature Flag 키. `LesInboundCancelBlockToggle.KEY`, 기본 OFF. */
export const LES_INBOUND_CANCEL_BLOCK_FLAG = 'feature.lesInboundCancel.block'

/** 입고현황 조회 응답 1행에서 판정에 쓰는 부분. */
export interface LesOriginCandidate {
  cutSeq: number | null
}

/**
 * 입고현황 조회(`UI_GRREPORT_DETAIL` 계열) 1행이 LES 유래인가.
 *
 * `cutSeq` 가 비어 오면 판정하지 않는다(false) — 값이 없는 것을 LES 로 읽으면 정상 건의 취소까지 막힌다.
 * BE 가드도 대상이 안 잡히면 아무 판단 없이 흘려보낸다(`CancelInboundLesOriginGuard`, LLD 3장).
 */
export const isLesOriginRow = (row: LesOriginCandidate): boolean => row.cutSeq === LES_ORIGIN_CUT_SEQ

/**
 * 입고예정(`OPR_AN_STATUS`) 1행이 LES 유래인가 — `srcTableNm` 이 실려 오는 경로 전용이다.
 *
 * 2.0 조회 응답(`get-all-inbounds` · `get-erp-inbounds`)에는 아직 이 값이 없다. BE 가 응답에 실어 주면
 * 파생축(`isLesOriginRow`) 대신 이쪽을 쓴다.
 */
export const isLesOriginPlan = (srcTableNm: string | null | undefined): boolean => srcTableNm === LES_ORIGIN_SRC_TABLE_NAME

/**
 * 선택한 행 중 하나라도 LES 유래면 취소를 막는다.
 *
 * BE 가드가 `targetPlans.any { it.isLesOrigin() }` 로 요청 전체를 거절하므로, 화면도 부분 취소로
 * 나누지 않고 같은 단위로 막는다 — 섞인 선택을 통과시키면 서버가 전체를 거절해 아무것도 안 지워진다.
 */
export const hasLesOriginRow = (rows: readonly LesOriginCandidate[]): boolean => rows.some(isLesOriginRow)
